import { useState, useEffect, useMemo } from 'react';
import { X, Search, Copy, FileText, Check } from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';

interface ApreciacaoResumo {
  id: string;
  numero?: string;
  titulo?: string;
  identificacaoMaquina?: string;
  maquina?: {
    fabricante?: string;
    modelo?: string;
    serie?: string;
    descricao?: string;
  };
  ativo?: {
    equipamento?: string;
    tag?: string;
  };
}

interface ModalCopiarDadosMaquinaProps {
  aberto: boolean;
  /** Apreciação atual — é excluída da lista para não copiar de si mesma. */
  apreciacaoIdAtual?: string;
  onFechar: () => void;
  /** Recebe os dados completos da apreciação selecionada (via buscarApreciacao). */
  onSelecionar: (dadosCompletos: any) => void;
}

export function ModalCopiarDadosMaquina({
  aberto,
  apreciacaoIdAtual,
  onFechar,
  onSelecionar,
}: ModalCopiarDadosMaquinaProps) {
  const { projeto } = useProjeto();
  const { t } = useI18n();
  const [termoBusca, setTermoBusca] = useState('');
  const [apreciacoes, setApreciacoes] = useState<ApreciacaoResumo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [carregandoId, setCarregandoId] = useState<string | null>(null);
  const [mostrarMais, setMostrarMais] = useState(false);
  const limiteExibicao = 10;

  useEffect(() => {
    if (aberto) {
      setTermoBusca('');
      setMostrarMais(false);
      carregarApreciacoes();
    }
  }, [aberto, projeto?.id]);

  const carregarApreciacoes = async () => {
    if (!projeto?.id) {
      setApreciacoes([]);
      return;
    }
    setCarregando(true);
    try {
      const response = await apiService.listarApreciacoes({ projetoId: projeto.id, limit: 10000 });
      if (response.success && response.data) {
        const lista = Array.isArray(response.data) ? response.data : [];
        setApreciacoes(lista);
      }
    } catch (error) {
      console.error('Erro ao carregar apreciações:', error);
    } finally {
      setCarregando(false);
    }
  };

  const apreciacoesFiltradas = useMemo(() => {
    const lista = apreciacoes.filter((a) => a.id !== apreciacaoIdAtual);
    if (!termoBusca.trim()) return lista;

    const termo = termoBusca.toLowerCase();
    return lista.filter((a) =>
      a.titulo?.toLowerCase().includes(termo) ||
      a.numero?.toLowerCase().includes(termo) ||
      a.identificacaoMaquina?.toLowerCase().includes(termo) ||
      a.maquina?.fabricante?.toLowerCase().includes(termo) ||
      a.maquina?.modelo?.toLowerCase().includes(termo) ||
      a.maquina?.descricao?.toLowerCase().includes(termo) ||
      a.ativo?.equipamento?.toLowerCase().includes(termo) ||
      a.ativo?.tag?.toLowerCase().includes(termo)
    );
  }, [apreciacoes, termoBusca, apreciacaoIdAtual]);

  const apreciacoesExibidas = mostrarMais
    ? apreciacoesFiltradas
    : apreciacoesFiltradas.slice(0, limiteExibicao);
  const temMaisResultados = apreciacoesFiltradas.length > limiteExibicao;

  const handleSelecionar = async (a: ApreciacaoResumo) => {
    setCarregandoId(a.id);
    try {
      const response = await apiService.buscarApreciacao(a.id);
      if (response.success && response.data) {
        onSelecionar(response.data);
        onFechar();
      } else {
        alert(response.message || t('apreciacaoModais.copiarDados.erroCarregarSelecionada'));
      }
    } catch (error) {
      console.error('Erro ao buscar apreciação para cópia:', error);
      alert(t('apreciacaoModais.copiarDados.erroCarregarSelecionada'));
    } finally {
      setCarregandoId(null);
    }
  };

  const getTituloExibicao = (a: ApreciacaoResumo): string =>
    a.titulo || a.identificacaoMaquina || a.maquina?.descricao || a.numero || t('apreciacaoModais.copiarDados.semTitulo');

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onFechar}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-schmersal-gray-darker rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-schmersal-blue px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <Copy className="h-6 w-6 text-white mr-2" />
              <h3 className="text-lg font-bold text-white">{t('apreciacaoModais.copiarDados.titulo')}</h3>
            </div>
            <button
              onClick={onFechar}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="px-6 py-4">
            <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mb-4">
              {t('apreciacaoModais.copiarDados.instrucao')}
            </p>

            {/* Campo de Busca */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                {t('apreciacaoModais.copiarDados.buscaRotulo')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-schmersal-gray-medium dark:text-schmersal-gray-light" />
                <input
                  type="text"
                  value={termoBusca}
                  onChange={(e) => {
                    setTermoBusca(e.target.value);
                    setMostrarMais(false);
                  }}
                  placeholder={t('apreciacaoModais.copiarDados.buscaPlaceholder')}
                  className="pl-10 block w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg
                           shadow-sm focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue
                           bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white
                           placeholder-schmersal-gray-medium dark:placeholder-schmersal-gray-medium
                           sm:text-sm py-2.5 transition-all duration-200"
                  autoFocus
                />
              </div>
            </div>

            {/* Lista de Resultados */}
            <div className="border border-rg-line dark:border-schmersal-gray-dark rounded-lg max-h-96 overflow-y-auto">
              {carregando ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-schmersal-blue"></div>
                  <p className="mt-3 text-sm text-schmersal-gray-medium dark:text-white font-medium">{t('apreciacaoModais.copiarDados.carregando')}</p>
                </div>
              ) : apreciacoesFiltradas.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-16 w-16 text-schmersal-gray-medium dark:text-schmersal-gray-light" />
                  <p className="mt-3 text-sm text-schmersal-gray-medium dark:text-white font-medium">
                    {termoBusca.trim()
                      ? t('apreciacaoModais.copiarDados.vazioBusca')
                      : t('apreciacaoModais.copiarDados.vazioLista')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-rg-hairline dark:divide-schmersal-gray-dark">
                    {apreciacoesExibidas.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        disabled={carregandoId !== null}
                        onClick={() => handleSelecionar(a)}
                        className="w-full text-left px-4 py-3 hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors disabled:opacity-50 disabled:cursor-wait"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-semibold text-schmersal-gray-dark dark:text-white">
                              {getTituloExibicao(a)}
                            </span>
                            <div className="mt-1 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                              {a.numero && <span>{t('apreciacaoModais.copiarDados.numero', { numero: a.numero })}</span>}
                              {a.maquina?.fabricante && (
                                <span className={a.numero ? ' • ' : ''}>{a.maquina.fabricante}</span>
                              )}
                              {a.maquina?.modelo && (
                                <span> {a.maquina.modelo}</span>
                              )}
                              {a.ativo?.tag && (
                                <span className=" • ">{t('apreciacaoModais.copiarDados.tag', { tag: a.ativo.tag })}</span>
                              )}
                            </div>
                          </div>
                          {carregandoId === a.id ? (
                            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-schmersal-blue shrink-0"></div>
                          ) : (
                            <Check className="h-5 w-5 text-schmersal-gray-medium dark:text-schmersal-gray-light shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {temMaisResultados && !mostrarMais && (
                    <div className="px-4 py-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark text-center">
                      <button
                        type="button"
                        onClick={() => setMostrarMais(true)}
                        className="px-4 py-2 text-sm font-medium text-schmersal-blue dark:text-white
                                 border border-schmersal-blue rounded-lg hover:bg-schmersal-blue hover:text-white
                                 dark:hover:bg-schmersal-blue transition-all duration-200"
                      >
                        {t('apreciacaoModais.lista.mostrarMais', { restantes: apreciacoesFiltradas.length - limiteExibicao })}
                      </button>
                    </div>
                  )}

                  {mostrarMais && temMaisResultados && (
                    <div className="px-4 py-3 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark text-center">
                      <button
                        type="button"
                        onClick={() => setMostrarMais(false)}
                        className="px-4 py-2 text-sm font-medium text-schmersal-gray-medium dark:text-schmersal-gray-light
                                 border border-rg-line dark:border-schmersal-gray-dark rounded-lg
                                 hover:bg-schmersal-gray-medium hover:text-white transition-all duration-200"
                      >
                        {t('apreciacaoModais.lista.mostrarMenos')}
                      </button>
                    </div>
                  )}

                  {termoBusca.trim() && (
                    <div className="px-4 py-2 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark bg-schmersal-gray-light dark:bg-schmersal-gray-dark">
                      <p className="text-xs text-center text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        {apreciacoesFiltradas.length === 1
                          ? t('apreciacaoModais.lista.resultadoUm', { total: apreciacoesFiltradas.length })
                          : t('apreciacaoModais.lista.resultadoVarios', { total: apreciacoesFiltradas.length })}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-schmersal-gray-light dark:bg-schmersal-gray-dark px-6 py-3 flex justify-end">
            <button
              type="button"
              onClick={onFechar}
              className="px-4 py-2 text-sm font-medium text-schmersal-gray-dark dark:text-white
                       border border-rg-line dark:border-schmersal-gray-dark rounded-lg
                       hover:bg-schmersal-gray-medium hover:text-white transition-all duration-200"
            >
              {t('comum.acao.cancelar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
